/**
 * SoundBud - Composable flower bud from spritesheet
 *
 * Layers multiple sprites to create an animated sound bud:
 * - Base plant
 * - 3 petal layers
 * - Glow effect
 *
 * Can open, close, pulse, and glow.
 */

import { Container, Sprite, Texture, Assets, Rectangle } from 'pixi.js';

export class SoundBud extends Container {
  private base: Sprite;
  private petal1: Sprite;
  private petal2: Sprite;
  private petal3: Sprite;
  private glow: Sprite;

  private frameHeight = 0;
  private isOpen = false;

  constructor() {
    super();

    // Sprites will be created after loading
    this.base = new Sprite();
    this.petal1 = new Sprite();
    this.petal2 = new Sprite();
    this.petal3 = new Sprite();
    this.glow = new Sprite();

    // Add in layering order
    this.addChild(this.base);
    this.addChild(this.petal1);
    this.addChild(this.petal2);
    this.addChild(this.petal3);
    this.addChild(this.glow);

    // Initially hide petals and glow (closed state)
    this.petal1.alpha = 0;
    this.petal2.alpha = 0;
    this.petal3.alpha = 0;
    this.glow.alpha = 0;
  }

  /**
   * Load and setup spritesheet
   */
  async load(): Promise<void> {
    // Load the spritesheet
    const texture = await Assets.load('/assets/introduction/sound-bud-spritesheet.png');

    // Calculate frame height (5 frames horizontal)
    this.frameHeight = texture.height;

    // Extract frames from spritesheet
    const frames = this.extractFrames(texture, 5);

    // Assign textures to sprites
    this.base.texture = frames[0]!;
    this.petal1.texture = frames[1]!;
    this.petal2.texture = frames[2]!;
    this.petal3.texture = frames[3]!;
    this.glow.texture = frames[4]!;

    // Center all sprites at exact same position (0,0) in container
    [this.base, this.petal1, this.petal2, this.petal3, this.glow].forEach(sprite => {
      sprite.anchor.set(0.5);
      sprite.x = 0;
      sprite.y = 0;
    });

    console.log('✓ Sound bud loaded (5 layers aligned)');
  }

  /**
   * Extract frames from horizontal spritesheet
   */
  private extractFrames(texture: Texture, count: number): Texture[] {
    const frames: Texture[] = [];
    const frameWidth = texture.width / count;
    const frameHeight = texture.height;

    for (let i = 0; i < count; i++) {
      const frame = new Texture({
        source: texture.source,
        frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight)
      });
      frames.push(frame);
    }

    return frames;
  }

  /**
   * Animate bud opening
   */
  async open(duration: number = 1000): Promise<void> {
    if (this.isOpen) return;

    console.log('🌸 Sound bud opening...');

    // Fade in petals sequentially
    await this.fadePetal(this.petal1, duration * 0.33);
    await this.fadePetal(this.petal2, duration * 0.33);
    await this.fadePetal(this.petal3, duration * 0.33);

    this.isOpen = true;
  }

  /**
   * Fade in a petal layer
   */
  private fadePetal(petal: Sprite, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const startAlpha = petal.alpha;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        petal.alpha = startAlpha + (1 - startAlpha) * progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Pulse glow effect
   * Returns animation function to add to ticker
   */
  startGlowPulse(): () => void {
    const pulseSpeed = 0.002;

    const animate = () => {
      if (!this.glow) return;

      const time = Date.now() * pulseSpeed;
      this.glow.alpha = 0.3 + Math.sin(time) * 0.2; // Pulse between 0.1 and 0.5
    };

    // Set initial glow visibility
    this.glow.alpha = 0.3;

    // Return animate function so it can be added to ticker
    return animate;
  }

  /**
   * Wiggle animation (when tapped)
   */
  async wiggle(): Promise<void> {
    const duration = 300;
    const startTime = Date.now();
    const startRotation = this.rotation;

    return new Promise((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Elastic wiggle
        const wiggleAmount = Math.sin(progress * Math.PI * 4) * (1 - progress) * 0.1;
        this.rotation = startRotation + wiggleAmount;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.rotation = startRotation;
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Set size (maintains aspect ratio)
   */
  setSize(size: number): void {
    this.scale.set(size / this.frameHeight);
  }
}
